import { memo, useCallback, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import "./SearchMyBooking.scss";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import { Form, Formik } from "formik";
import InputFormik from "app/pages/.shared/form/InputFormik";
import {
	defaultValues,
	validationSchema,
} from "app/pages/Account/SearchMyBooking/SearchMyBookingFormSchema";
import AppGlobalsContext from "app/AppGlobalsContext";
import RelativeLink from "app/pages/.shared/RelativeLink";
import PropTypes from "prop-types";
import { ALERT_TYPE, BRANDS } from "app/constants";
import RecoverBookingDataContainer from "app/pages/CorporateSite/RecoverBookingData/RecoverBookingDataContainer";
import IconCrossDark from "app/pages/.shared/static/icons/IconCrossDark";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import LogoBrandContainer from "app/pages/.shared/LogoBrand/LogoBrandContainer";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { isEmpty } from "lodash";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { BackButton } from "app/components/ui/buttons/BackButton";

const SearchMyBooking = ({
	onFormSuccess = () => {},
	myBookingError,
	setMyBookingError = () => {},
	isWhiteLabel,
}) => {
	const [showBookingForm, setShowBookingForm] = useState(true);
	const handleSubmit = useCallback((values, actions) => {
		actions.setSubmitting(false);
		onFormSuccess(values.booking, values.email);
	}, []);

	const onClickCross = useCallback(() => {
		setShowBookingForm(true);
	}, []);

	const handleRecoverLinkClick = useCallback(() => {
		setShowBookingForm(false);
	}, []);

	const { brand } = useContext(AppGlobalsContext);

	const isBrandEH = brand === BRANDS.EK;

	return (
		<div className="search-my-booking">
			<div className="search-my-booking__header">
				<div className="search-my-booking__header-back">
					<BackButton
						onClick={() => {
							window.history.back();
						}}
					/>
				</div>
				<RelativeLink
					to={{ pathname: "/" }}
					className={classNames({
						"search-my-booking__header__logo": true,
						"search-my-booking__header__logo-small": isBrandEH,
					})}
				>
					<LogoBrandContainer />
				</RelativeLink>
			</div>
			{showBookingForm ? (
				<div className="search-my-booking__body">
					<header className="search-my-booking__headline">
						<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
							<FormattedMessage id="search.my.booking.headline" tagName="h3" />
						</Typography>
					</header>
					<Formik
						enableReinitialize
						initialValues={defaultValues}
						validateOnChange={false}
						validateOnBlur={false}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
					>
						{({ errors, isSubmitting, setStatus, setFieldError, submitCount }) => {
							return (
								<>
									<Form
										className="search-my-booking__form"
										onChange={event => {
											setStatus({});
											if (!isEmpty(errors)) {
												setFieldError(event?.target?.name);
											}

											setMyBookingError(null);
										}}
									>
										<FormErrorMessages
											errors={errors}
											isSubmitting={isSubmitting}
											submitCount={submitCount}
										/>

										<InputFormik
											id="booking"
											data-testid="booking-reference-input"
											name="booking"
											maxLength={7}
											type="number"
											label={
												<FormattedMessage id="search.my.booking.booking.input.label" />
											}
										/>

										<InputFormik
											id="email"
											data-testid="email-input"
											name="email"
											type="text"
											label={
												<FormattedMessage id="search.my.booking.email.input.label" />
											}
										/>

										<Button
											variant={BUTTON_VARIANTS.BORDERLESS}
											size={BUTTON_SIZES.SMALL}
											className="search-my-booking__forgotten-infos"
											onClick={handleRecoverLinkClick}
											data-testid="missing-info"
											label={
												<FormattedMessage id="search.my.booking.forgotten.label" />
											}
										/>
										<div className="search-my-booking__form-footer">
											{myBookingError && (
												<AlertMessage
													alertType={ALERT_TYPE.ERROR}
													message={
														<FormattedMessage id={myBookingError} />
													}
												/>
											)}
											<Button
												variant={BUTTON_VARIANTS.FILLED}
												size={BUTTON_SIZES.LARGE}
												submit
												className="search-my-booking__button"
												loading={isSubmitting}
												data-testid="search-button"
												label={
													<FormattedMessage id="search.my.booking.cta.label" />
												}
											/>
										</div>
									</Form>
								</>
							);
						}}
					</Formik>
					{isWhiteLabel && (
						<div className="search-my-booking__footer">
							<Button
								variant={BUTTON_VARIANTS.LINK}
								className="nav__item-link"
								to={{ pathname: "/booking-auth" }}
								data-testid="switch-to-booking-auth-link"
								label={<FormattedMessage id="search.my.booking.footer.label" />}
								icon={<IconRight />}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
							/>
						</div>
					)}
				</div>
			) : (
				<div className="search-my-booking__recover">
					<div className="search-my-booking__form-recover">
						<div className="search-my-booking__cross" onClick={onClickCross}>
							<IconCrossDark />
						</div>
						<RecoverBookingDataContainer onRecoverSuccess={onClickCross} />
					</div>
				</div>
			)}
		</div>
	);
};

SearchMyBooking.propTypes = {
	onFormSuccess: PropTypes.func,
	myBookingError: PropTypes.string,
	isWhiteLabel: PropTypes.bool,
	setMyBookingError: PropTypes.func,
};

export default memo(SearchMyBooking);
